import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/components/ui/Icon';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { toast } from '@/utils/toast';
import { useTimezone } from '@/contexts/TimezoneContext';
import { statusService } from '@/services/status.service';
import { AdminFeatureRequest, FeatureRequestType, FeatureRequestStatus, UpdateFeatureRequestData } from '@/types/status';

/**
 * Feature Request Manager
 *
 * Admin interface for triaging user feature requests
 */

const TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'feature', label: 'Feature', icon: 'bulb' },
  { value: 'bug', label: 'Bug', icon: 'caution' },
  { value: 'improvement', label: 'Improvement', icon: 'spark' },
  { value: 'other', label: 'Other', icon: 'chat-bubble' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'new', label: 'Submitted' },
  { value: 'reviewed', label: 'Under Review' },
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'declined', label: 'Declined' },
];

const STATUS_UPDATE_OPTIONS = [
  { value: 'new', label: 'Submitted', icon: 'mail-open' },
  { value: 'reviewed', label: 'Under Review', icon: 'search' },
  { value: 'planned', label: 'Planned', icon: 'calendar' },
  { value: 'in_progress', label: 'In Progress', icon: 'sync' },
  { value: 'completed', label: 'Completed', icon: 'tick-circle' },
  { value: 'declined', label: 'Declined', icon: 'cross-circle' },
];

const TYPE_BADGE_CONFIG: Record<FeatureRequestType, { color: string; bgColor: string; icon: string }> = {
  feature: { color: 'text-green-700', bgColor: 'bg-green-100', icon: ICONS.BULB },
  bug: { color: 'text-red-700', bgColor: 'bg-red-100', icon: ICONS.CAUTION },
  improvement: { color: 'text-primary-800', bgColor: 'bg-primary-50', icon: ICONS.SPARK },
  other: { color: 'text-gray-700', bgColor: 'bg-gray-100', icon: ICONS.CHAT },
};

const STATUS_BADGE_CONFIG: Record<FeatureRequestStatus, { color: string; bgColor: string }> = {
  new: { color: 'text-gray-700', bgColor: 'bg-gray-100' },
  reviewed: { color: 'text-blue-700', bgColor: 'bg-blue-100' },
  planned: { color: 'text-purple-700', bgColor: 'bg-purple-100' },
  in_progress: { color: 'text-orange-700', bgColor: 'bg-orange-100' },
  completed: { color: 'text-green-700', bgColor: 'bg-green-100' },
  declined: { color: 'text-red-700', bgColor: 'bg-red-100' },
};

export const FeatureRequestManager = () => {
  const queryClient = useQueryClient();
  const { formatDateOnly } = useTimezone();

  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [editingRequest, setEditingRequest] = useState<Record<number, { status: string; adminNotes: string }>>({});

  // Fetch feature requests
  const { data: requests, isLoading } = useQuery({
    queryKey: ['admin-feature-requests'],
    queryFn: statusService.getAdminFeatureRequests,
    staleTime: 60 * 1000,
  });

  // Update feature request mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateFeatureRequestData }) =>
      statusService.updateFeatureRequest(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feature-requests'] });
      toast.success('Feature request updated');
      setEditingRequest({});
    },
    onError: (error: Error) => {
      toast.error(`Failed to update request: ${error.message}`);
    },
  });

  const toggleRow = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleEdit = (request: AdminFeatureRequest, field: 'status' | 'adminNotes', value: string) => {
    setEditingRequest((prev) => ({
      ...prev,
      [request.id]: {
        status: field === 'status' ? value : (prev[request.id]?.status ?? request.status),
        adminNotes: field === 'adminNotes' ? value : (prev[request.id]?.adminNotes ?? request.adminNotes ?? ''),
      },
    }));
  };

  const handleSave = (request: AdminFeatureRequest) => {
    const edited = editingRequest[request.id];
    if (!edited) return;

    updateMutation.mutate({
      id: request.id,
      data: {
        status: edited.status !== request.status ? (edited.status as FeatureRequestStatus) : undefined,
        adminNotes: edited.adminNotes !== (request.adminNotes ?? '') ? edited.adminNotes : undefined,
      },
    });
  };

  const hasChanges = (request: AdminFeatureRequest): boolean => {
    const edited = editingRequest[request.id];
    if (!edited) return false;

    return (
      edited.status !== request.status ||
      edited.adminNotes !== (request.adminNotes ?? '')
    );
  };

  // Filter requests
  const filteredRequests = requests?.filter((request: AdminFeatureRequest) => {
    if (typeFilter !== 'all' && request.type !== typeFilter) return false;
    if (statusFilter !== 'all' && request.status !== statusFilter) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name={ICONS.LOADER} size="lg" color="text-gray-400" ariaHidden />
        <span className="ml-2 text-gray-600">Loading feature requests...</span>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Feature Requests</h2>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="w-48">
            <Select
              options={TYPE_OPTIONS}
              value={typeFilter}
              onValueChange={setTypeFilter}
              placeholder="Filter by type"
              size="sm"
            />
          </div>
          <div className="w-48">
            <Select
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onValueChange={setStatusFilter}
              placeholder="Filter by status"
              size="sm"
            />
          </div>
        </div>

        {/* Requests Table */}
        <div className="space-y-2">
          {filteredRequests?.map((request: AdminFeatureRequest) => {
            const typeConfig = TYPE_BADGE_CONFIG[request.type];
            const statusConfig = STATUS_BADGE_CONFIG[request.status];
            const isExpanded = expandedRows.has(request.id);
            const edited = editingRequest[request.id];

            return (
              <div key={request.id} className="border border-gray-200 rounded-xl overflow-hidden">
                {/* Main Row */}
                <div
                  className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleRow(request.id)}
                >
                  <Icon
                    name={isExpanded ? ICONS.ANGLE_DOWN : ICONS.ANGLE_RIGHT}
                    size="sm"
                    color="text-gray-400"
                    ariaHidden
                  />
                  <div className="flex-1 min-w-0 grid grid-cols-5 gap-4 items-center">
                    <div>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium',
                          typeConfig.color,
                          typeConfig.bgColor
                        )}
                      >
                        <Icon name={typeConfig.icon} size="sm" color={typeConfig.color} ariaHidden />
                        {request.type}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <p className="font-medium text-gray-900 truncate">{request.title}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 truncate">{request.user?.fullName || 'Unknown'}</p>
                      <p className="text-xs text-gray-500 truncate">{request.user?.email}</p>
                      <p className="text-xs text-gray-400 truncate">{request.team?.name || 'No team'}</p>
                    </div>
                    <div className="text-right">
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium',
                          statusConfig.color,
                          statusConfig.bgColor
                        )}
                      >
                        {STATUS_UPDATE_OPTIONS.find((o) => o.value === request.status)?.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div
                    className="p-4 bg-gray-50 border-t border-gray-200 space-y-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{request.description}</p>
                    </div>

                    {request.screenshot && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Screenshot
                        </label>
                        <a
                          href={request.screenshot}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-600 hover:text-primary-700"
                        >
                          View Screenshot →
                        </a>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Select
                          label="Status"
                          options={STATUS_UPDATE_OPTIONS}
                          value={edited?.status ?? request.status}
                          onValueChange={(value) => handleEdit(request, 'status', value)}
                        />
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>Submitted: {formatDateOnly(request.createdAt)}</p>
                      </div>
                    </div>

                    <div>
                      <Textarea
                        label="Admin Notes (Internal)"
                        value={edited?.adminNotes ?? request.adminNotes ?? ''}
                        onChange={(e) => handleEdit(request, 'adminNotes', e.target.value)}
                        placeholder="Internal notes for tracking"
                        rows={3}
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={() => handleSave(request)}
                        disabled={!hasChanges(request) || updateMutation.isPending}
                        className={cn(
                          'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                          hasChanges(request)
                            ? 'bg-primary-600 text-primary-600-contrast hover:bg-primary-700'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        )}
                      >
                        {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {(!filteredRequests || filteredRequests.length === 0) && (
          <p className="text-center text-gray-500 py-8">
            {typeFilter || statusFilter ? 'No requests match the filters' : 'No feature requests'}
          </p>
        )}
      </div>
    </div>
  );
};
